import {
  getDocs,
  where,
  query,
  getDoc,
  doc,
  onSnapshot
} from "firebase/firestore";

import FormField from "@/models/FormField";
import {callApi} from "@/api";
import { cityRepository } from "@/data";
import { createCityId, normalizeStaffReplyToEmail } from "@strenforcement/domain/city";
import {CurrentTaskRun, LastTaskRun, Task} from "@/models/Task";
import {Property} from "@/models/Property";
import {Listing} from "@/models/Listing";
import {PersistentModel} from "@/models/PersistentModel";
import {License} from "@/models/License";
import {compareStatuses} from "@/models/LicenseStatus";
import {LicenseWorkflow} from "@/models/LicenseWorkflow";
import {
  calculateExpirationDate,
  EXPIRATION_TYPE_END_OF_YEAR,
  EXPIRATION_TYPE_ONE_YEAR
} from "@/models/LicenseExpirationType";
import {
  LicenseApprovalEmailMode,
  normalizeLicenseApprovalEmail
} from "@strenforcement/domain/licenseApprovalEmail";
import { normalizeLicenseStatusEmails } from "@strenforcement/domain/licenseStatusEmail";
import { municipalityPrintableLicense } from "@strenforcement/domain/printableLicense";
import { resolveLicensePeriodStartMonthDay } from '@strenforcement/domain/licenseExpiration';

function normalizeEmailList(value) {
  const emails = Array.isArray(value)
    ? value
    : (typeof value === 'string' ? value.split(/[,;\n]+/g) : []);

  const seen = new Set();
  return emails
    .filter(email => typeof email === 'string')
    .map(email => email.trim())
    .filter(email => {
      const normalized = email.toLowerCase();
      if (!email || seen.has(normalized)) {
        return false;
      }
      seen.add(normalized);
      return true;
    });
}

export class City extends PersistentModel {

  static collectionName = 'cities';

  constructor(doc) {
    super(doc);
    this._data.admin = {
      url: '',
      sealUrl: '',
      formalName: '',
      officeTitle: '',
      address: '',
      contactTitle: '',
      contactName: '',
      contactEmail: '',
      staffReplyToEmail: '',
      contactPhone: '',
      violationLetterTitle: '',
      violationLetterSubtitle: '',
      ...this.admin
    };
    this._data.general = {
      treatInactiveLicenseAsViolation: false,
      ...this.general
    };
    this._data.licenseWorkflow = {
      ...this._data.licenseWorkflow
    };
    this._data.licenseApprovalEmail = normalizeLicenseApprovalEmail(this._data.licenseApprovalEmail);
    if (!this._data.printableLicense && this._data.licenseStatusEmails?.rules?.some(rule => rule.certificate)) {
      this._data.printableLicense = municipalityPrintableLicense(this._data);
    }
    if (this._data.licenseStatusEmails !== undefined) {
      this._data.licenseStatusEmails = normalizeLicenseStatusEmails(this._data.licenseStatusEmails);
    }
  }

  _update(data) {
    if (this.isNew) {
      data.city = data.name.toLowerCase();
      data.id = createCityId(data.city, this.state);
      this._data = { ...this._data, ...data };
      return cityRepository.updateCity(this.id, this._data, { merge: true });
    } else {
      return cityRepository.updateCity(this.id, data || this._data, { merge: true });
    }
  }

  save() {
    let data = this._data;
    data.city = data.name.toLowerCase();
    data.id = createCityId(this.name, this.state);
    return cityRepository.updateCity(this.id, data, { merge: true });
  }

  get isComplete() {
    return !!this.name;
  }

  get placeId() {
    return this._data.placeId;
  }

  set placeId(placeId) {
    this._data.placeId = placeId;
  }

  get staticMapUrl() {
    return `https://maps.googleapis.com/maps/api/staticmap?center=${this.latlng.lat},${this.latlng.lng}&zoom=12&size=240x240&key=AIzaSyDOqYud38oaZms1EpdoLsxoBA63oDD-wes`;
  }

  get url() {
    return this._data.url;
  }

  set url(url) {
    this._data.url = url;
  }

  get name() {
    return this._get('name');
  }
  set name(name) {
    this._set('name', name);
  }

  get state() {
    return this._get('state') || 'ME'; // Default to Maine
  }
  set state(state) {
    this._set('state', state);
  }

  get zipCode() {
    return this._get('zipCode');
  }

  get label() {
    if (this.name) {
      return `${this.name}, ${this.state}`;
    } else {
      return '';
    }
  }

  get status() {
    return this._data.status;
  }

  get visibility() {
    return this._data.visibility;
  }
  set visibility(visibility) {
    this._set('visibility', visibility);
  }

  get lastLoadId() {
    return this._get('lastLoadId');
  }

  get paused() {
    return this._get('paused') || false;
  }

  set paused(paused) {
    this._set('paused', paused);
  }

  updatePausedState(paused) {
    return this._update({paused})
    .then(() => {
      this._set('paused', paused);
    });
  }

  get latlng() {
    return this._data.latlng;
  }

  set latlng(latlng) {
    this._data.latlng = latlng;
  }

  updateLocation({name, state, placeId, latlng}) {
    let data = {name, state, placeId, latlng};
    Object.keys(data).forEach(key => {
      if (data[key] === null) delete data[key];
    });
    if (name) {
      data.city = name.toLowerCase();
    }
    return this._update(data)
    .then(() => this._data = { ...this._data, ...data });
  }

  get idFieldNameForApi() {
    return 'cityId';
  }

  get dateChecked() {
    return this._data.dateChecked;
  }

  get branding() {
    return this._data.branding || {};
  }

  get backgroundColor() {
    return this.branding.backgroundColor;
  }
  set backgroundColor(backgroundColor) {
    this.branding.backgroundColor = backgroundColor;
  }

  get iconUrl() {
    return this.branding.iconUrl;
  }
  set iconUrl(iconUrl) {
    this.branding.iconUrl = iconUrl;
  }

  updateBranding(branding) {
    return this._update({ branding })
    .then(() => {
      this._set('branding', { ...this.branding, ...branding });
    });
  }

  get admin() {
    return this._data.admin || {};
  }

  /** Saves administrative information, validating the optional staff reply-to address. */
  async updateAdmin(admin) {
    if (Object.prototype.hasOwnProperty.call(admin, 'staffReplyToEmail')) {
      const email = admin.staffReplyToEmail ?? '';
      const normalizedEmail = normalizeStaffReplyToEmail(email);

      if (typeof email !== 'string' || (email.trim() && !normalizedEmail)) {
        throw new Error('Enter one valid staff reply-to email address, or leave it blank.');
      }

      admin = {
        ...admin,
        staffReplyToEmail: normalizedEmail || ''
      };
    }

    await this._update({ admin });
    this._set('admin', { ...this.admin, ...admin });
  }

  get general() {
    return this._data.general || {};
  }

  updateGeneral(general) {
    return this._update({ general })
    .then(() => {
      this._set('general', { ...this.general, ...general });
    });
  }

  get townSealUrl() {
    return this.admin.sealUrl || null;
  }
  set townSealUrl(townSealUrl) {
    this.admin.sealUrl = townSealUrl;
  }

  get townHallAddress() {
    return this.admin.address || '--Town Hall Address--';
  }
  set townHallAddress(townHallAddress) {
    this.admin.address = townHallAddress;
  }

  get townClerkName() {
    return this.admin.clerk || '--Town Clerk Name--';
  }
  set townClerkName(townClerkName) {
    this.admin.clerk = townClerkName;
  }

  get ordinanceName() {
    return this.admin.ordinance || '--Ordinance Number and Name--';
  }
  set ordinanceName(ordinanceName) {
    this.admin.ordinance = ordinanceName;
  }

  get formalName() {
    return this.admin.formalName || `Town of ${this.name}`;
  }
  set formalName(formalName) {
    this.admin.formalName = formalName;
  }

  get officeTitle() {
    return this.admin.officeTitle || 'Office of the Town Clerk';
  }
  set officeTitle(officeTitle) {
    this.admin.officeTitle = officeTitle;
  }

  get contactTitle() {
    return this.admin.contactTitle || 'Town Clerk';
  }
  set contactTitle(contactTitle) {
    this.admin.contactTitle = contactTitle;
  }

  get contactName() {
    return this.admin.contactName || this.townClerkName;
  }
  set contactName(contactName) {
    this.admin.contactName = contactName;
  }

  get contactPhone() {
    return this.admin.contactPhone;
  }
  set contactPhone(contactPhone) {
    this.admin.contactPhone = contactPhone;
  }

  get contactEmail() {
    return this.admin.contactEmail;
  }
  set contactEmail(contactEmail) {
    this.admin.contactEmail = contactEmail;
  }

  get violationLetterTitle() {
    return this.admin.violationLetterTitle;
  }
  set violationLetterTitle(violationLetterTitle) {
    this.admin.violationLetterTitle = violationLetterTitle;
  }

  get violationLetterSubtitle() {
    return this.admin.violationLetterSubtitle;
  }
  set violationLetterSubtitle(violationLetterSubtitle) {
    this.admin.violationLetterSubtitle = violationLetterSubtitle;
  }

  get treatInactiveLicenseAsViolation() {
    return this.general.treatInactiveLicenseAsViolation || false;
  }
  set treatInactiveLicenseAsViolation(treatInactiveLicenseAsViolation) {
    this.general.treatInactiveLicenseAsViolation = treatInactiveLicenseAsViolation;
  }

  get minNightStayExemptionThreshold() {
    return this.general.minNightStayExemptionThreshold || null;
  }
  set minNightStayExemptionThreshold(minNightStayExemptionThreshold) {
    this.general.minNightStayExemptionThreshold = minNightStayExemptionThreshold;
  }

  isListingExempt(listing) {
    // If the city exempts listings with a minimum night stay above a certain threshold, then check if this listing's
    // minimum night stay is above it.
    if (this.minNightStayExemptionThreshold && listing.minNightStay) {
      if (listing.minNightStay > this.minNightStayExemptionThreshold) {
        return true;
      }
    }
    return false;
  }

  get flags() {
    return this._get('flags');
  }

  get imageCachingEnabled() {
    return this._get('imageCachingEnabled') === true;
  }

  addFlag(flag) {
    let flags = this.flags;
    if (flags.indexOf(flag) <= 0) {
      flags.push(flag);
      this._set('flags', flags);
    }
  }

  removeFlag(flag) {
    this._set('flags', this.flags.filter(f => f !== flag));
  }

  get expirationType() {
    const type = this._get('licenseExpirationType');
    if (type) {
      return type;
    } else if (this.id === 'casco_me' || this.id === 'south portland_me') {
      return EXPIRATION_TYPE_ONE_YEAR;
    } else {
      return EXPIRATION_TYPE_END_OF_YEAR;
    }
  }

  calculateLicenseExpirationDate(/* Date */ dateIssued) {
    return calculateExpirationDate(dateIssued, this.expirationType, this.licenseIssuedEarlyAllowanceMonths, this.licensePeriodStartMonthDay, this.licenseFirstPeriodStartsOn);
  }

  get licenseFirstPeriodStartsOn() {
    return this.data.licenseFirstPeriodStartsOn ?? null;
  }

  get licensePeriodStartMonthDay() {
    return resolveLicensePeriodStartMonthDay(this.data);
  }

  get licenseIssuedEarlyAllowanceMonths() {
    return this._get('licenseIssuedEarlyAllowanceMonths') ?? 1;
  }
  set licenseIssuedEarlyAllowanceMonths(licenseIssuedEarlyAllowanceMonths) {
    this._set('licenseIssuedEarlyAllowanceMonths', licenseIssuedEarlyAllowanceMonths);
  }

  get customFields() {
    return this._get('licenseApplicationForm.customFields') || [];
  }

  get licenseTypes() {
    const licenseTypes = this._get('licenseTypes');
    return Array.isArray(licenseTypes) ? licenseTypes : [];
  }

  get isLicenseListPublic() {
    return this._get('isLicenseListPublic') || false;
  }

  get licenseWorkflow() {
    return new LicenseWorkflow(this);
  }

  get licenseStatuses() {
    return [...this.licenseWorkflow.statuses].sort(compareStatuses);
  }

  get defaultLicenseStatus() {
    return this.licenseWorkflow.defaultStatusCode;
  }

  // @deprecated
  addCustomField() {
    const fields = this.customFields;
    fields.push(new FormField());
    this._set('customFields', fields);
  }

  // @deprecated
  removeCustomField(id) {
    const fields = this.customFields.filter(field => field.id !== id);
    this._set('customFields', fields);
  }

  // @deprecated
  updateCustomFields(customFields) {
    return this._update({
      customFields: customFields.map(field => field._data)
    })
    .then(() => {
      this._data.customFields = customFields;
    });
  }

  async updateLicenseApplicationConfirmation({ confirmationText, paymentUrl, paymentLabel }) {
    await this._update({
      licenseApplicationConfirmationText: confirmationText,
      licenseApplicationPaymentUrl: paymentUrl,
      licenseApplicationPaymentLabel: paymentLabel
    });
    this._data.licenseApplicationConfirmationText = confirmationText;
    this._data.licenseApplicationPaymentUrl = paymentUrl;
    this._data.licenseApplicationPaymentLabel = paymentLabel;
  }

  async updateLicenseApplicationStaffNotificationEmails(emails) {
    const licenseApplicationStaffNotificationEmails = normalizeEmailList(emails);
    await this._update({ licenseApplicationStaffNotificationEmails });
    this._data.licenseApplicationStaffNotificationEmails = licenseApplicationStaffNotificationEmails;
  }

  get licenseApplicationConfirmationText() {
    return this._get('licenseApplicationConfirmationText')
      || `${ this.name } staff will review your application and will be in touch soon regarding its status.`;
  }
  get licenseApplicationPaymentUrl() {
    return this._get('licenseApplicationPaymentUrl') || null;
  }
  get licenseApplicationPaymentLabel() {
    return this._get('licenseApplicationPaymentLabel') || null;
  }
  get licenseApplicationStaffNotificationEmails() {
    return normalizeEmailList(this._get('licenseApplicationStaffNotificationEmails'));
  }

  get licenseApprovalEmail() {
    return normalizeLicenseApprovalEmail(this._get('licenseApprovalEmail'));
  }

  get licenseApprovalEmailMode() {
    return this.licenseApprovalEmail.mode || LicenseApprovalEmailMode.disabled;
  }

  get licenseApprovalEmailCustomMessage() {
    return this.licenseApprovalEmail.customMessage || null;
  }

  get licenseApprovalEmailTriggerStatusCode() {
    return this.licenseApprovalEmail.triggerStatusCode || null;
  }

  get licenseStatusEmails() {
    return normalizeLicenseStatusEmails(this._get('licenseStatusEmails'), {
      legacyApprovalEmail: this._get('licenseApprovalEmail'),
      statusConfig: this.licenseStatuses
    });
  }

  get hasStoredLicenseStatusEmails() {
    return this._get('licenseStatusEmails') !== undefined;
  }

  get licenseStatusEmailRules() {
    return this.licenseStatusEmails.rules;
  }

  get printableLicense() {
    return municipalityPrintableLicense(this._data);
  }

  async updatePrintableLicense(printableLicense) {
    const response = await cityRepository.updateCityPrintableLicense(this.id, { printableLicense });
    this._data.printableLicense = response.printableLicense;
  }

  previewPrintableLicense(printableLicense) {
    return cityRepository.previewCityPrintableLicense(this.id, { printableLicense });
  }

  async updateLicenseStatusEmails(licenseStatusEmails) {
    const response = await cityRepository.updateCityLicenseStatusEmails(this.id, {
      licenseStatusEmails
    });
    const responseSettings = response?.city?.data?.licenseStatusEmails;
    if (response?.city?.data?.printableLicense) this._data.printableLicense = response.city.data.printableLicense;
    this._data.licenseStatusEmails = normalizeLicenseStatusEmails(
      responseSettings || licenseStatusEmails
    );
  }

  previewLicenseStatusEmailDraft(rule) {
    return cityRepository.previewCityLicenseStatusEmailDraft(this.id, { rule });
  }

  async updateLicenseApprovalEmailSettings(settings) {
    const response = await cityRepository.updateCityLicenseApprovalEmailSettings(this.id, {
      licenseApprovalEmail: settings
    });
    const responseSettings = response?.city?.data?.licenseApprovalEmail;
    this._data.licenseApprovalEmail = normalizeLicenseApprovalEmail(responseSettings || {
      ...this.licenseApprovalEmail,
      ...settings
    });
  }

  updateLicenseApprovalEmailMode(mode) {
    return this.updateLicenseApprovalEmailSettings({ mode });
  }

  updateLicenseApplicationForm(licenseApplicationForm) {
    const formData = { ...licenseApplicationForm };
    delete formData.licenseApplicationForm;
    let promise = Promise.resolve(null);
    if (formData.customFields) {
      promise = this._update({customFields: []});
    }
    return promise
    .then(() => this._update({
      licenseApplicationForm: formData
    }))
    .then(() => {
      this._data.licenseApplicationForm = {
        ...this._data.licenseApplicationForm,
        ...formData
      };
      delete this._data.licenseApplicationForm.licenseApplicationForm;
    });
  }

  async updateLicenseWorkflow(licenseWorkflow) {
    const response = await cityRepository.updateCityLicenseWorkflowStatuses(this.id, licenseWorkflow);
    if (response?.city?.data) {
      this._data = {
        ...this._data,
        ...response.city.data
      };
    }
    this._data.licenseWorkflow = {
      ...this._data.licenseWorkflow,
      ...licenseWorkflow
    };
  }

  async updateLicenseExpirationPolicy({ licenseExpirationType, licensePeriodStartMonthDay, licenseFirstPeriodStartsOn, licenseIssuedEarlyAllowanceMonths } = {}) {
    const response = await cityRepository.updateCityLicenseExpirationPolicy(this.id, {
      licenseExpirationType,
      licensePeriodStartMonthDay,
      licenseFirstPeriodStartsOn,
      licenseIssuedEarlyAllowanceMonths
    });
    if (response?.city?.data) {
      this._data = {
        ...this._data,
        ...response.city.data
      };
    } else {
      if (licenseExpirationType !== undefined) {
        this._data.licenseExpirationType = licenseExpirationType;
      }
      if (licensePeriodStartMonthDay !== undefined) {
        this._data.licensePeriodStartMonthDay = licensePeriodStartMonthDay;
      }
      if (licenseFirstPeriodStartsOn !== undefined) {
        this._data.licenseFirstPeriodStartsOn = licenseFirstPeriodStartsOn;
      }
      if (licenseIssuedEarlyAllowanceMonths !== undefined) {
        this._data.licenseIssuedEarlyAllowanceMonths = licenseIssuedEarlyAllowanceMonths;
      }
    }
  }

  get geoJson() {
    const geoJsonString = this._get('boundary.geoJson');
    if (geoJsonString) {
      return JSON.parse(geoJsonString);
    } else {
      return null;
    }
  }

  async fetchBoundary() {
    let response = await callApi('importCityBoundary', {cityId: this.id});
    if (response) {
      return true;
    } else {
      return false;
    }
  }

  get recordsSourceType() {
    return this._get('recordsSourceType');
  }

  set recordsSourceType(recordsSourceType) {
    this._set('recordsSourceType', recordsSourceType);
  }

  get customRecordsSourceUrl() {
    return this._get('customRecordsSourceUrl');
  }

  set customRecordsSourceUrl(customRecordsSourceUrl) {
    this._set('customRecordsSourceUrl', customRecordsSourceUrl);
  }

  get propertyBoundariesUrl() {
    return this._get('propertyBoundariesUrl');
  }

  updateRecordsType(type, customUrl) {
    customUrl = customUrl || '';
    return this._update({
      recordsSourceType: type,
      customRecordsSourceUrl: customUrl
    })
    .then(() => {
      this.recordsSourceType = type;
      this.customRecordsSourceUrl = customUrl;
    });
  }

  getRecordsUrlForType(type) {
    switch (type) {
      case 'cama':
        return `https://jeodonnell.com/cama/${this.id.replace(/_me/, '')}`;
      case 'vision':
        return `https://gis.vgsi.com/${this.id.replace(/[_ ]/g, '')}`;
      case 'mapgeo':
        return `https://${this.id.replace(/[_ ]/g, '').toLowerCase()}.mapgeo.io/`;
      default:
        return this.customRecordsSourceUrl;
    }
  }

  get recordsUrl() {
    let type = this.recordsSourceType;
    return this.getRecordsUrlForType(type);
  }

  onTask(taskName, handler) {
    return Task.onOne(this, taskName, handler);
  }

  onCurrentTaskRun(taskName, handler) {
    return onSnapshot(
      doc(this.db, `cities/${this.id}/tasks/${taskName}/runs/current`),
      doc => handler(new CurrentTaskRun(doc.data()))
    )
  }

  onLastTaskRun(taskName, handler) {
    return onSnapshot(
      doc(this.db, `cities/${this.id}/tasks/${taskName}/runs/last`),
      doc => handler(new LastTaskRun(doc.data()))
    )
  }

  async callSearchApi(namespace, klass, {page, pageSize, sort, sortAscending, filters, searchField, searchValue}) {
    return await callApi('search', {
      cityId: this.id,
      page, pageSize, sort, sortAscending, filters, searchField, searchValue
    }, namespace)
      .then(/* { items: Array, total: number, page: number, pageSize: number } */result => {
        result.items = result.items.map(klass.dataConverter(this, namespace));
        return result;
      });
  }

  getProperties({page, pageSize, sort, sortAscending, filters, searchField, searchValue}) {
    if (searchField === 'address.street' || searchField === 'owner') {
      searchValue = (searchValue || '').toUpperCase();
    }
    return this.callSearchApi('properties', Property, {
      sort: sort || 'address.street',
      page, pageSize, sortAscending, filters, searchField, searchValue
    })
  }

  findNearbyProperties({lat, lng, radius, limit}) {
    return Property.findNearby(this, {lat, lng, radius, limit});
  }

  // @deprecated Not used
  getListings({page, pageSize, sort, sortAscending, filters, searchField, searchValue}) {
    return this.callSearchApi('listings', Listing, {
      sort: sort || 'listing.title',
      page, pageSize, sortAscending, filters, searchField, searchValue
    });
  }

  get canRequestPropertyRecordImport() {
    return !!this.recordsSourceType;
  }

  get canImportPropertiesAutomatically() {
    return this.recordsSourceType && this.recordsSourceType !== 'custom';
  }

  async searchProperties(query, field) {
    if (field === 'propertyId') {
      const property = await Property.one(this, query);
      return property ? [property] : [];
    } else if (field === 'licenseId') {
      const license = await License.one(this, query)
      return license ? [license.property] : [];
    } else {
      return await Property.search(this, query, field);
    }
  }

  async normalizeProperty(address) {
    let properties = await Property.search(this, address);

    if (properties.length > 0) {
      return properties;
    } else {
      console.log('Trying a google search for the property');
      properties = this.normalizePropertiesWithGoogle(address);

      return properties;
    }
  }

  async normalizePropertiesWithGoogle(address) {
    const addresses = await this.normalizeAddress(address);

    if (addresses.length === 0) {
      return [];
    }
    const addressIds = addresses.map(address => address.id);
    const {docs} = await getDocs(
      query(
        this.subCollection('properties'),
        where('googlePlace.id', 'in', addressIds)
      )
    );

    const properties = docs.map(Property.docConverter(this));

    return properties;
  }

  normalizeAddress(address) {
    return cityRepository.geocodeAddress(address, {
      state: this.state,
      locality: this.name
    });
  }

  async getProperty(id) {
    return getDoc(this.subDoc('properties', id))
    .then(Property.docConverter(this));
  }

  async canImportLicenses() {
    return callApi('isLicenseImportSupported', {cityId:this.id}, 'data');
  }

  getLetters() {
    if (this.id === 'old_orchard_beach_me') {
      return [
        {
          label: 'Notice of Compliance',
          description: 'Informational notice for compliant properties',
          template: 'oob',
          options: {type: 'compliance'}
        },
        {
          label: 'Notice of Violation',
          description: 'Sent to owner\'s home address',
          template: 'oob',
          options: {
            type: 'violation',
            destination: 'home'
          }
        },
        {
          label: 'Notice of Violation',
          description: 'Sent to the rental address',
          template: 'oob',
          options: {
            type: 'violation',
            destination: 'rental'
          }
        }
      ];
    } else if (this.id === 'south portland_me') {
      return [
        {
          label: 'First Notice',
          description: 'Initial notice of non-compliance',
          template: 'sopo',
          options: {
            first: true
          }
        },
        {
          label: 'Second Notice',
          description: 'Follow-up notice for unresolved violations',
          template: 'sopo',
          options: {
            first: false
          }
        }
      ];
    } else if (this.id === 'portland_me') {
      return [
        {
          label: 'Notice of Violation and Order to Correct',
          description: 'For unregistered short-term rentals',
          template: 'portland',
          options: {
            first: true,
            registered: false
          }
        },
        {
          label: 'Notice of Intent to Prosecute',
          description: 'For unregistered short-term rentals',
          template: 'portland',
          options: {
            first: false,
            registered: false
          }
        },
        {
          label: 'Notice of Violation and Order to Correct',
          description: 'For registered short-term rentals',
          template: 'portland',
          options: {
            first: true,
            registered: true
          },
          licenseRequired: true
        },
        {
          label: 'Notice of Intent to Prosecute',
          description: 'For registered short-term rentals',
          template: 'portland',
          options: {
            first: false,
            registered: true
          },
          licenseRequired: true
        }
      ];
    } else if (this.id === 'falmouth_me') {
      return [
        {
          label: 'First Notice',
          description: 'Initial notice of non-compliance',
          template: 'falmouth',
          options: {
            first: true
          }
        },
        {
          label: 'Second Notice',
          description: 'Follow-up notice for unresolved violations',
          template: 'falmouth',
          options: {
            first: false
          }
        }
      ];
    } else if (this.id === 'freeport_me') {
      return [
        {
          label: 'First Notice',
          description: 'Initial notice of non-compliance',
          template: 'freeport',
          options: {
            first: true
          }
        },
        {
          label: 'Second Notice',
          description: 'Follow-up notice for unresolved violations',
          template: 'freeport',
          options: {
            first: false
          }
        }
      ];
    } else if (this.id === 'casco_me') {
      return [
        {
          label: 'Registration Renewal Reminder',
          description: 'Annual short-term rental registration fee reminder and payment instructions',
          template: 'casco',
          options: {}
        },
        {
          label: 'Violation Letter',
          description: 'Initial notice of non-compliance',
          template: 'casco_violation',
          options: {}
        }
      ];
    } else if (this.id === 'kennebunk_me') {
      return [
        {
          label: 'Request for Voluntary Compliance',
          description: 'Initial outreach requesting voluntary registration',
          template: 'default',
          options: {voluntary: true}
        }
      ];
    } else if (this.id === 'cape elizabeth_me') {
      return [
        {
          label: 'Violation Letter',
          description: 'Initial notice of non-compliance',
          template: 'cape_elizabeth'
        }
      ];
    } else {
      return [
        {
          label: 'Violation Letter',
          description: 'Includes listing screenshot as evidence',
          template: 'default',
          options: { screenshot: true }
        },{
          label: 'Violation Letter',
          description: 'Text-only, no screenshot attached',
          template: 'default',
          options: { screenshot: false }
        }
      ];
    }
  }

  get isDemoMode() {
    return this.data?.mode === 'demo';
  }

  get isFrontendV2Ready() {
    const explicit = this._get('frontend_v2_ready');
    if (explicit === true || explicit === false) {
      return explicit;
    }
    const flags = this.flags;
    return Array.isArray(flags) && flags.includes('frontend_v2_ready');
  }

  /**
   * Return a summary of license/violation/listing counts, with the following shape:
   *    licenses:
   *      total
   *      active
   *      expired
   *      pending
   *      expiresBy:
   *        [date]
   *    listings:
   *      total
   *      active
   *      unverified
   *      verified
   *      whitelisted:
   *        total
   *        unverified
   *        verified
   *      unwhitelisted:
   *        total
   *        unverified
   *        verified
   *    properties:
   *      total
   *      violation (non-whitelisted/non-deferred)
   *      violationTotal (includes whitelisted/deferred)
   *      whitelisted (deferred violations)
   * @return {Promise<{}>}
   */
  async fetchSummary({ view } = {}) {
    const { fetchApi } = await import('@/api');
    const params = new URLSearchParams();
    if (view) {
      params.set('view', view);
    }
    const query = params.toString();
    return fetchApi(`/v1/cities/${this.id}/metrics${query ? `?${query}` : ''}`);
  }
}
